/**
 * Horse Dash — tap-faster-to-win race.
 *
 * Rules
 *   - Each player gets one horse on its own lane.
 *   - Every tap (touch / mouse / Space / Enter) advances that horse a
 *     small amount.
 *   - Tap *fast* to chain combos. Inside the combo window each tap bumps
 *     the player's combo counter; missing the window resets it.
 *   - Combo tiers:
 *         counter ≥  4 → tier 1 — "GOOD!"     (×1.25 speed)
 *         counter ≥  8 → tier 2 — "GREAT!"    (×1.5 speed)
 *         counter ≥ 12 → tier 3 — "AWESOME!"  (×1.8 speed)
 *         counter ≥ 16 → tier 4 — "ON FIRE!"  (×2.2 speed, fiery aura)
 *   - First horse to reach 100% wins the race.
 *
 * Architecture
 *   - synchronized mode — host owns the truth, broadcasts a snapshot at
 *     ~15 Hz, clients render directly off the snapshot. The snapshot
 *     carries each player's progress (0-1), combo counter, last-tap
 *     timestamp and combo tier so every peer renders the same HUD.
 *   - taps are routed as one-shot SDK events ('hd-tap'). The host queues
 *     them per tick and resolves them in `tick()` so combo bookkeeping
 *     stays authoritative.
 *   - rendering is peer-local: galloping animation, dust trails, tap
 *     ripples and combo flares are spawned in response to snapshot deltas
 *     so the visual feedback feels instant on every device even when the
 *     network jitters.
 */
import { SdkGameScene } from '@platform/sdk';

// ─────────────────────────────────────────────────────────── tuning

// Race tuning
const TRACK_LENGTH = 1.0;          // 0..1, full distance to the finish line
const TAP_BASE_PROGRESS = 0.012;   // fraction of the track per regular tap

// Combo system
const COMBO_WINDOW_MS = 360;       // taps within this window keep the chain alive
const COMBO_TIERS = [
  { min: 0,  mult: 1.0,  label: '',           color: 0xffffff, glow: 0x000000 },
  { min: 4,  mult: 1.25, label: 'GOOD!',      color: 0xfacc15, glow: 0xfde68a },
  { min: 8,  mult: 1.5,  label: 'GREAT!',     color: 0xfb923c, glow: 0xfed7aa },
  { min: 12, mult: 1.8,  label: 'AWESOME!',   color: 0xec4899, glow: 0xfbcfe8 },
  { min: 16, mult: 2.2,  label: 'ON FIRE!',   color: 0xef4444, glow: 0xfecaca }
];

// Phase timings (ms)
const COUNTDOWN_MS = 3200;
const END_MS       = 5400;

// Per-lane palette
const LANE_COLORS = [
  0xef4444, 0x3b82f6, 0x10b981, 0xfacc15,
  0xec4899, 0x8b5cf6, 0x06b6d4, 0xf97316
];

// Phase enum
const P_COUNTDOWN = 'countdown';
const P_RACE      = 'race';
const P_END       = 'end';

// HUD layout
const HUD_PILL_HEIGHT = 32;
const HUD_PILL_GAP    = 6;
const HUD_TOP         = 12;
const HUD_SIDE        = 12;

// ─────────────────────────────────────────────────────────── scene

export default class GameScene extends SdkGameScene {

  start({ players }) {
    this.W = this.scale.width;
    this.H = this.scale.height;

    // Build player lookup tables (id → name, id → color, id → laneIndex)
    // from the roster. Lane index drives both the visual lane order and
    // the snapshot ordering so peers render the same arrangement.
    this._players = (players || this.sdk.players || []).slice();
    this._playerNames = {};
    this._playerColors = {};
    this._playerLane = {};
    for (let i = 0; i < this._players.length; i++) {
      const p = this._players[i];
      this._playerNames[p.id] = p.name || p.id;
      this._playerColors[p.id] = LANE_COLORS[i % LANE_COLORS.length];
      this._playerLane[p.id] = i;
    }

    // Authoritative state. Host writes; clients overwrite via applySnapshot.
    // Per-player record: { progress, combo, comboTier, lastTapAt, taps }
    this.state = {
      phase: P_COUNTDOWN,
      phaseAt: 0,
      phaseLen: COUNTDOWN_MS,
      racers: this._initRacers(),
      winnerId: null,
      finishOrder: [],
      matchOver: false
    };

    // Host-only: queue of taps received this tick.
    this._tapQueue = [];

    if (this.sdk.isHost) {
      this._unsubTap = this.sdk.on('hd-tap', (payload) => {
        if (payload && payload.playerId) {
          this._tapQueue.push({ playerId: payload.playerId, ts: payload.ts || Date.now() });
        }
      });
    }

    // Last seen progress per player — used by every peer to spawn dust /
    // confetti when a horse jumps forward, and to detect combo tier
    // transitions for the cheer SFX.
    this._lastProgress = {};
    this._lastComboTier = {};
    for (const id of Object.keys(this._playerNames)) {
      this._lastProgress[id] = 0;
      this._lastComboTier[id] = 0;
    }

    // Cache current dimensions — used by onResize() to decide whether
    // to rebuild the HUD / lane layout.
    this._lastResizeW = this.W;
    this._lastResizeH = this.H;

    // Scene construction
    this._buildBackground();
    this._buildTrack();
    this._buildHorses();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._buildEncouragement();

    this._setupLocalInput();
    this._startMusic();

    this.events.once('shutdown', () => this._teardown());
  }

  _initRacers() {
    const out = {};
    for (const p of this._players) {
      out[p.id] = {
        progress: 0,
        combo: 0,
        comboTier: 0,
        lastTapAt: 0,
        taps: 0,
        finished: false,
        finishedAt: 0
      };
    }
    return out;
  }

  // ─────────────────────────────────────────────────── host simulation

  tick(dt, _inputs) {
    if (!this.sdk.isHost) return;
    if (this.state.matchOver) return;

    this.state.phaseAt += dt * 1000;
    const nowMs = Date.now();

    // Combo decay: if a player has not tapped within the combo window,
    // their combo resets to 0 and so does the tier. We process this
    // every tick so the HUD on every peer reflects the decay even when
    // they stop tapping.
    if (this.state.phase === P_RACE) {
      for (const id of Object.keys(this.state.racers)) {
        const r = this.state.racers[id];
        if (r.combo > 0 && (nowMs - r.lastTapAt) > COMBO_WINDOW_MS) {
          r.combo = 0;
          r.comboTier = 0;
        }
      }
    }

    // Drain queued taps. Outside the RACE phase taps are simply discarded
    // — pressing during the countdown shouldn't give anyone an early lead.
    if (this.state.phase === P_RACE) {
      while (this._tapQueue.length) {
        const tap = this._tapQueue.shift();
        this._applyTap(tap.playerId, tap.ts);
      }
    } else {
      this._tapQueue.length = 0;
    }

    switch (this.state.phase) {
      case P_COUNTDOWN:
        if (this.state.phaseAt >= this.state.phaseLen) this._enterPhase(P_RACE, 0);
        break;

      case P_RACE:
        // Match ends as soon as somebody crosses the finish line. We let
        // the END phase play its little victory animation before
        // calling endMatch().
        if (this.state.winnerId) {
          this._enterPhase(P_END, END_MS);
        }
        break;

      case P_END:
        if (this.state.phaseAt >= this.state.phaseLen) this._finalizeMatch();
        break;
    }
  }

  _enterPhase(phase, lenMs) {
    const prev = this.state.phase;
    this.state.phase = phase;
    this.state.phaseAt = 0;
    this.state.phaseLen = lenMs;
    this._onPhaseChanged(prev, phase);
  }

  _applyTap(playerId, ts) {
    const r = this.state.racers[playerId];
    if (!r || r.finished) return;

    // Combo bookkeeping. If the gap since the last tap is short enough we
    // bump the combo counter; otherwise reset and start a new chain.
    if (ts - r.lastTapAt <= COMBO_WINDOW_MS) {
      r.combo += 1;
    } else {
      r.combo = 1;
    }
    r.lastTapAt = ts;
    r.taps += 1;

    // Resolve the active combo tier (highest tier whose `min` is met).
    let tier = 0;
    for (let i = COMBO_TIERS.length - 1; i >= 0; i--) {
      if (r.combo >= COMBO_TIERS[i].min) { tier = i; break; }
    }
    r.comboTier = tier;
    const mult = COMBO_TIERS[tier].mult;

    r.progress = Math.min(TRACK_LENGTH, r.progress + TAP_BASE_PROGRESS * mult);

    if (r.progress >= TRACK_LENGTH) {
      r.progress = TRACK_LENGTH;
      r.finished = true;
      r.finishedAt = this.state.phaseAt;
      this.state.finishOrder.push(playerId);
      if (!this.state.winnerId) this.state.winnerId = playerId;
    }
  }

  _finalizeMatch() {
    if (this.state.matchOver) return;
    this.state.matchOver = true;

    // Final scores: each player's score is their *taps* count, with the
    // race winner displayed as the actual winner. The finish order list
    // is broadcast in case GameOver wants to render a podium someday.
    const scores = {};
    let winnerName = 'tie';
    for (const id of Object.keys(this.state.racers)) {
      scores[id] = this.state.racers[id].taps;
    }
    if (this.state.winnerId) {
      winnerName = this._playerNames[this.state.winnerId] || this.state.winnerId;
    }
    this.sdk.endMatch({ winnerName, scores });
  }

  // ─────────────────────────────────────────────────── snapshots

  snapshot() {
    // Compact per-racer payload — keep this lean since it ships at
    // ~15 Hz over the WebRTC channel.
    const racers = {};
    for (const id of Object.keys(this.state.racers)) {
      const r = this.state.racers[id];
      racers[id] = {
        p: round3(r.progress),
        c: r.combo,
        t: r.comboTier,
        l: r.lastTapAt,
        x: r.taps,
        f: r.finished ? 1 : 0
      };
    }
    return {
      ph: this.state.phase,
      pa: this.state.phaseAt,
      pl: this.state.phaseLen,
      r:  racers,
      w:  this.state.winnerId,
      o:  this.state.finishOrder.slice(),
      m:  this.state.matchOver ? 1 : 0
    };
  }

  applySnapshot(snap) {
    if (!snap) return;
    const prevPhase = this.state.phase;
    this.state.phase = snap.ph;
    this.state.phaseAt = snap.pa;
    this.state.phaseLen = snap.pl;
    this.state.winnerId = snap.w || null;
    this.state.finishOrder = Array.isArray(snap.o) ? snap.o.slice() : [];
    this.state.matchOver = !!snap.m;

    if (snap.r) {
      for (const id of Object.keys(snap.r)) {
        let r = this.state.racers[id];
        if (!r) {
          r = { progress: 0, combo: 0, comboTier: 0, lastTapAt: 0, taps: 0, finished: false, finishedAt: 0 };
          this.state.racers[id] = r;
        }
        r.progress = snap.r[id].p;
        r.combo = snap.r[id].c;
        r.comboTier = snap.r[id].t;
        r.lastTapAt = snap.r[id].l;
        r.taps = snap.r[id].x;
        r.finished = !!snap.r[id].f;
      }
    }

    if (prevPhase !== this.state.phase) this._onPhaseChanged(prevPhase, this.state.phase);
  }

  _onPhaseChanged(prev, next) {
    if (next === P_RACE) {
      this._playSfx('hd_go');
      this.cameras.main.shake(180, 0.006);
      this._spawnStartBurst();
    } else if (next === P_END) {
      this._playSfx('hd_finish');
      this._spawnFireworks();
    }
  }

  _playSfx(key) {
    if (this.sdk.audio && this.sdk.audio.playSfx) {
      try { this.sdk.audio.playSfx(key); } catch (_) { /* ignore */ }
    }
  }

  // ─────────────────────────────────────────── scene construction

  _buildBackground() {
    const W = this.W, H = this.H;
    const g = this.add.graphics().setDepth(0);

    // Sky gradient — cool dawn into warm horizon.
    const stops = [
      { y: 0.00, color: 0x0f172a },
      { y: 0.30, color: 0x1e293b },
      { y: 0.55, color: 0xf59e0b },
      { y: 0.62, color: 0xfb923c }
    ];
    for (let i = 0; i < stops.length - 1; i++) {
      g.fillStyle(stops[i].color, 1);
      g.fillRect(0, stops[i].y * H, W, (stops[i + 1].y - stops[i].y) * H + 2);
    }

    // Sun on the horizon.
    g.fillStyle(0xffe4a8, 0.65);
    g.fillCircle(W * 0.78, H * 0.55, 70);
    g.fillStyle(0xfff7e0, 0.8);
    g.fillCircle(W * 0.78, H * 0.55, 42);
    g.fillStyle(0xffffff, 0.95);
    g.fillCircle(W * 0.78, H * 0.55, 22);

    // Distant rolling hills (silhouette).
    g.fillStyle(0x312e81, 0.85);
    this._fillHill(g, [
      [0, H * 0.62],
      [W * 0.15, H * 0.58],
      [W * 0.30, H * 0.61],
      [W * 0.50, H * 0.56],
      [W * 0.72, H * 0.60],
      [W * 0.88, H * 0.57],
      [W, H * 0.62],
      [W, H * 0.66],
      [0, H * 0.66]
    ]);
    g.fillStyle(0x1e1b4b, 0.85);
    this._fillHill(g, [
      [0, H * 0.66],
      [W * 0.20, H * 0.63],
      [W * 0.40, H * 0.66],
      [W * 0.60, H * 0.62],
      [W * 0.85, H * 0.65],
      [W, H * 0.66],
      [W, H * 0.70],
      [0, H * 0.70]
    ]);

    // Star bokeh up top.
    for (let i = 0; i < 22; i++) {
      const x = (i * 73.1 + 30) % W;
      const y = ((i * 41.3) % (H * 0.30)) + 12;
      const a = 0.4 + ((i * 17) % 10) / 20;
      g.fillStyle(0xffffff, a);
      g.fillCircle(x, y, 1.2);
    }
  }

  _fillHill(g, pts) {
    g.beginPath();
    g.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) g.lineTo(pts[i][0], pts[i][1]);
    g.closePath();
    g.fillPath();
  }

  _buildTrack() {
    const W = this.W, H = this.H;

    // The track lives in the lower portion of the screen. Each player
    // gets a horizontal lane drawn between trackTop and trackBottom.
    const trackTop = H * 0.66;
    const trackBottom = H - 6;
    const margin = 64; // distance from each edge for the start / finish

    this._trackTop = trackTop;
    this._trackBottom = trackBottom;
    this._startX = margin;
    this._finishX = W - margin;

    const g = this.add.graphics().setDepth(1);

    // Brown earth strip (the actual track).
    g.fillStyle(0x78350f, 1);
    g.fillRect(0, trackTop, W, trackBottom - trackTop);
    g.fillStyle(0x92400e, 1);
    g.fillRect(0, trackTop, W, 8);
    g.fillStyle(0x451a03, 0.6);
    g.fillRect(0, trackBottom - 6, W, 6);

    // Lane stripes.
    const ids = Object.keys(this._playerNames);
    const n = Math.max(1, ids.length);
    const laneH = (trackBottom - trackTop) / n;
    this._laneH = laneH;
    for (let i = 0; i < n; i++) {
      const y = trackTop + i * laneH;
      // Alternating tint.
      g.fillStyle(i % 2 === 0 ? 0x92400e : 0x7c2d12, 0.35);
      g.fillRect(0, y, W, laneH);
      // Lane separator (subtle dotted line).
      if (i > 0) {
        g.fillStyle(0xfde68a, 0.4);
        for (let x = 16; x < W; x += 20) {
          g.fillRect(x, y - 1, 10, 2);
        }
      }
    }

    // Start line.
    g.fillStyle(0xffffff, 0.9);
    g.fillRect(this._startX - 4, trackTop, 4, trackBottom - trackTop);
    g.lineStyle(2, 0x000000, 0.4);
    g.strokeRect(this._startX - 4, trackTop, 4, trackBottom - trackTop);

    // Finish line — checkered pattern.
    const sq = 8;
    for (let y = trackTop; y < trackBottom; y += sq) {
      for (let dx = 0; dx < 16; dx += sq) {
        const cellRow = Math.floor((y - trackTop) / sq);
        const cellCol = dx / sq;
        const black = (cellRow + cellCol) % 2 === 0;
        g.fillStyle(black ? 0x111827 : 0xffffff, 1);
        g.fillRect(this._finishX - 8 + dx, y, sq, sq);
      }
    }
  }

  _buildHorses() {
    // Horses are simple cartoon shapes drawn into a Phaser Container so
    // we can move them as a unit, rotate the legs and pulse the body
    // without rebuilding the graphics every frame.
    this.horses = {};
    const ids = Object.keys(this._playerNames);
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i];
      const color = this._playerColors[id];
      const laneCenter = this._trackTop + this._laneH * (i + 0.5);
      const c = this.add.container(this._startX, laneCenter).setDepth(20);

      // Body
      const g = this.add.graphics();
      // legs (drawn first, behind the body)
      g.fillStyle(0x451a03, 1);
      g.fillRoundedRect(-12, 6, 4, 14, 1.5);
      g.fillRoundedRect(8,  6, 4, 14, 1.5);
      // body
      g.fillStyle(color, 1);
      g.fillEllipse(0, 0, 38, 22);
      g.fillStyle(0x000000, 0.18);
      g.fillEllipse(2, 4, 32, 12);
      // neck
      g.fillStyle(color, 1);
      g.beginPath();
      g.moveTo(12, -2);
      g.lineTo(20, -16);
      g.lineTo(28, -14);
      g.lineTo(18, 4);
      g.closePath();
      g.fillPath();
      // head
      g.fillStyle(color, 1);
      g.fillEllipse(26, -16, 14, 9);
      // ear
      g.fillStyle(color, 1);
      g.beginPath();
      g.moveTo(22, -22);
      g.lineTo(24, -16);
      g.lineTo(20, -16);
      g.closePath();
      g.fillPath();
      // eye
      g.fillStyle(0x000000, 1);
      g.fillCircle(28, -17, 1.4);
      // mane
      g.fillStyle(0x111827, 1);
      g.fillEllipse(15, -10, 10, 6);
      g.fillEllipse(11, -6, 10, 5);
      // tail
      g.fillStyle(0x111827, 1);
      g.beginPath();
      g.moveTo(-18, -2);
      g.lineTo(-30, -8);
      g.lineTo(-26, 4);
      g.closePath();
      g.fillPath();

      c.add(g);

      // Aura ring (only visible when combo tier > 0)
      const aura = this.add.graphics();
      aura.setAlpha(0);
      c.add(aura);

      // Player name above the horse
      const label = this.add.text(0, -32, (this._playerNames[id] || '').slice(0, 10), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '12px',
        fontStyle: 'bold',
        color: '#ffffff',
        stroke: '#000000',
        strokeThickness: 3
      }).setOrigin(0.5);
      c.add(label);

      this.horses[id] = {
        container: c,
        graphics: g,
        aura: aura,
        baseLane: laneCenter,
        gallop: 0,
        // Used by the renderer to interpolate visually toward the
        // authoritative progress so the horse glides forward instead of
        // teleporting on each tap.
        renderProgress: 0
      };
    }
  }

  _buildParticleLayers() {
    this.fxGraphics = this.add.graphics().setDepth(15);
    /** @type {Array<object>} */
    this._particles = [];
  }

  _buildHud() {
    const W = this.W;
    this.hudContainer = this.add.container(0, 0).setDepth(100);
    this.hudRows = {};

    const ids = Object.keys(this._playerNames);
    const n = ids.length;

    const twoCols = n > 4;
    const innerMargin = HUD_SIDE;
    const leftCount = twoCols ? Math.ceil(n / 2) : n;
    const pillWidth = twoCols
      ? (W - innerMargin * 3) / 2
      : W - innerMargin * 2;

    for (let i = 0; i < n; i++) {
      const id = ids[i];
      const isLeft = i < leftCount;
      const colIndex = isLeft ? i : (i - leftCount);
      const xOrigin = isLeft ? innerMargin : (innerMargin * 2 + pillWidth);
      const y = HUD_TOP + colIndex * (HUD_PILL_HEIGHT + HUD_PILL_GAP);
      const row = this._buildHudRow(id, xOrigin, y, pillWidth);
      this.hudContainer.add(row.group);
      this.hudRows[id] = row;
    }
  }

  _buildHudRow(playerId, originX, originY, width) {
    const color = this._playerColors[playerId] || 0xffffff;
    const name = this._playerNames[playerId] || playerId;

    const group = this.add.container(originX, originY);

    // Pill background
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.45);
    bg.fillRoundedRect(0, 0, width, HUD_PILL_HEIGHT, 14);
    bg.lineStyle(2, color, 0.95);
    bg.strokeRoundedRect(0, 0, width, HUD_PILL_HEIGHT, 14);
    group.add(bg);

    // Colour tag
    const tag = this.add.graphics();
    tag.fillStyle(color, 1);
    tag.fillRoundedRect(6, 5, 22, 22, 11);
    tag.lineStyle(2, 0xffffff, 0.9);
    tag.strokeRoundedRect(6, 5, 22, 22, 11);
    group.add(tag);

    // Player name
    const nameText = this.add.text(34, HUD_PILL_HEIGHT / 2, String(name).slice(0, 8), {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    group.add(nameText);

    // Progress bar — track + filled portion. Layout-wise it sits in the
    // middle stretchy area between the name and the right-aligned combo
    // readout.
    const barLeft = 100;
    const barRight = width - 60;
    const barWidth = Math.max(20, barRight - barLeft);
    const barH = 12;
    const barY = (HUD_PILL_HEIGHT - barH) / 2;

    const barTrack = this.add.graphics();
    barTrack.fillStyle(0xffffff, 0.18);
    barTrack.fillRoundedRect(barLeft, barY, barWidth, barH, 6);
    group.add(barTrack);

    const barFill = this.add.graphics();
    group.add(barFill);

    // Right side: combo number "x12"
    const comboText = this.add.text(width - 8, HUD_PILL_HEIGHT / 2, 'x0', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(1, 0.5);
    group.add(comboText);

    return {
      group, bg, barTrack, barFill, comboText, color,
      barLeft, barWidth, barH, barY,
      shownProgress: 0,
      shownCombo: 0
    };
  }

  _renderHud() {
    if (!this.hudRows) return;
    for (const id of Object.keys(this.hudRows)) {
      const row = this.hudRows[id];
      const r = this.state.racers[id];
      if (!r) continue;

      // Progress bar
      const fillW = row.barWidth * Math.max(0, Math.min(1, r.progress));
      row.barFill.clear();
      row.barFill.fillStyle(row.color, 1);
      row.barFill.fillRoundedRect(row.barLeft, row.barY, Math.max(0, fillW), row.barH, 6);
      // Subtle gloss line
      row.barFill.fillStyle(0xffffff, 0.35);
      row.barFill.fillRect(row.barLeft, row.barY + 2, Math.max(0, fillW), 2);

      // Combo readout — pulse when the combo number changes.
      if (r.combo !== row.shownCombo) {
        row.shownCombo = r.combo;
        row.comboText.setText('x' + r.combo);
        const tier = COMBO_TIERS[r.comboTier] || COMBO_TIERS[0];
        row.comboText.setColor('#' + (tier.color === 0xffffff ? 'ffffff' : tier.color.toString(16).padStart(6, '0')));
        if (r.combo > 0) {
          this.tweens.killTweensOf(row.comboText);
          row.comboText.setScale(1.4);
          this.tweens.add({
            targets: row.comboText,
            scale: 1,
            duration: 220,
            ease: 'Back.out'
          });
        }
      }
    }
  }

  _buildOverlayText() {
    const W = this.W, H = this.H;

    this.statusText = this.add.text(W / 2, H * 0.30, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '30px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#0f172a',
      strokeThickness: 6,
      align: 'center'
    }).setOrigin(0.5).setDepth(200);

    this.countdownText = this.add.text(W / 2, H * 0.42, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '160px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#dc2626',
      strokeThickness: 12
    }).setOrigin(0.5).setDepth(300);

    this.instructionsText = this.add.text(W / 2, H - 14,
      'TAP THE SCREEN AS FAST AS YOU CAN!', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '13px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 1).setDepth(200);
  }

  _buildEncouragement() {
    // A floating "GREAT! / AWESOME! / ON FIRE!" call-out that flashes
    // whenever the local player crosses a higher combo tier. Big and
    // bold so it can't be missed even on a phone.
    this.encourageText = this.add.text(this.W / 2, this.H * 0.50, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '64px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 8,
      align: 'center'
    }).setOrigin(0.5).setDepth(310).setAlpha(0);
  }

  /** Rebuild HUD + lane layout when the viewport changes. */
  onResize(width, height) {
    if (!width || !height) return;
    if (width === this._lastResizeW && height === this._lastResizeH) return;
    this._lastResizeW = width;
    this._lastResizeH = height;
    this.W = width;
    this.H = height;

    // Tear everything down and rebuild from the new dimensions.
    this._destroyContainer('hudContainer');

    // Background, track, horses are not stored in a single container, so
    // wipe by clearing the children list directly. Phaser doesn't expose
    // a "remove all my graphics" hook, but we can just nuke the display
    // list and rebuild it.
    this.children.removeAll(true);
    this._particles = [];

    this._buildBackground();
    this._buildTrack();
    this._buildHorses();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._buildEncouragement();
  }

  _destroyContainer(prop) {
    const obj = this[prop];
    if (obj && obj.destroy) {
      try { obj.destroy(true); } catch (_) { /* ignore */ }
    }
    this[prop] = null;
  }

  // ─────────────────────────────────────────── per-frame rendering

  update(time, delta) {
    // Clients smooth the phase timer between snapshots so animations
    // don't wait for the next host message to progress.
    if (!this.sdk.isHost && !this.state.matchOver) {
      this.state.phaseAt += delta;
    }
    const dt = delta / 1000;
    this._renderHud();
    this._renderHorses(dt);
    this._renderEncouragement();
    this._renderCountdown();
    this._renderStatusText();
    this._updateParticles(dt);
    this._detectComboTransitions();
  }

  _renderHorses(dt) {
    const xRange = this._finishX - this._startX;
    for (const id of Object.keys(this.horses)) {
      const horse = this.horses[id];
      const r = this.state.racers[id];
      if (!r) continue;

      // Smooth the rendered progress toward the authoritative number so
      // the horse glides forward instead of snapping on each tap.
      const target = r.progress;
      const lerp = Math.min(1, dt * 8);
      horse.renderProgress += (target - horse.renderProgress) * lerp;

      const x = this._startX + horse.renderProgress * xRange;
      horse.container.x = x;

      // Galloping bounce — speed scales with combo tier so a player on
      // fire visibly bounces harder.
      const tier = COMBO_TIERS[r.comboTier] || COMBO_TIERS[0];
      const recentTap = (Date.now() - r.lastTapAt) < COMBO_WINDOW_MS;
      const gallopRate = recentTap ? (10 + r.comboTier * 4) : 0;
      horse.gallop += dt * gallopRate;
      const bounce = Math.sin(horse.gallop) * (recentTap ? 4 + r.comboTier : 0);
      horse.container.y = horse.baseLane + bounce;

      // Lean slightly forward when on a hot streak.
      horse.container.rotation = recentTap ? -0.05 - r.comboTier * 0.01 : 0;

      // Aura ring scales / fades with combo tier.
      horse.aura.clear();
      if (r.comboTier > 0) {
        const pulse = 1 + Math.sin(horse.gallop * 0.6) * 0.12;
        horse.aura.lineStyle(4, tier.color, 0.85);
        horse.aura.strokeCircle(0, 0, 28 * pulse);
        horse.aura.lineStyle(2, tier.glow, 0.55);
        horse.aura.strokeCircle(0, 0, 36 * pulse);
      }

      // Spawn dust trails when the horse moves forward.
      const moved = (target - this._lastProgress[id]) > 0.0001;
      if (moved && this.state.phase === P_RACE) {
        this._spawnDust(x - 14, horse.baseLane + 14, tier);
        if (r.comboTier >= 3) {
          // Fiery sparks for high combos
          this._spawnFireSparks(x - 14, horse.baseLane + 8, tier);
        }
      }
      this._lastProgress[id] = target;
    }
  }

  _detectComboTransitions() {
    // Whenever a player crosses up into a higher combo tier, fire a
    // bright cheer + screen flash. We do this on every peer (host
    // included) so the local player gets instant feedback.
    for (const id of Object.keys(this.state.racers)) {
      const r = this.state.racers[id];
      const last = this._lastComboTier[id] || 0;
      if (r.comboTier > last) {
        this._onComboLevelUp(id, r.comboTier);
      } else if (r.comboTier < last && last > 0 && r.combo === 0) {
        // The chain just broke — soft "uh oh" feedback for the local
        // player only (no global noise).
        if (id === this.sdk.localPlayerId) {
          this._spawnTapRipple(this.W / 2, this.H / 2, 0xffffff, 0.4);
        }
      }
      this._lastComboTier[id] = r.comboTier;
    }
  }

  _onComboLevelUp(playerId, tierIdx) {
    const tier = COMBO_TIERS[tierIdx];
    if (!tier || tierIdx === 0) return;

    if (playerId === this.sdk.localPlayerId) {
      // Big banner for the local player.
      this._showEncouragement(tier.label, tier.color);
      this._playSfx(tierIdx >= 3 ? 'hd_cheer' : 'hd_combo');
      this.cameras.main.shake(120 + tierIdx * 30, 0.004 + tierIdx * 0.001);
    } else {
      this._playSfx('hd_combo');
    }

    // Burst around the leveling-up horse so every peer sees who hit it.
    const horse = this.horses[playerId];
    if (horse) {
      this._spawnComboBurst(horse.container.x, horse.container.y, tier);
    }
  }

  _showEncouragement(text, color) {
    if (!this.encourageText) return;
    this.encourageText.setText(text);
    this.encourageText.setColor('#' + color.toString(16).padStart(6, '0'));
    this.encourageText.setStroke('#000000', 8);
    this.tweens.killTweensOf(this.encourageText);
    this.encourageText.setAlpha(1);
    this.encourageText.setScale(1.6);
    this.encourageText.y = this.H * 0.50;
    this.tweens.add({
      targets: this.encourageText,
      scale: 1,
      duration: 220,
      ease: 'Back.out'
    });
    this.tweens.add({
      targets: this.encourageText,
      y: this.H * 0.42,
      alpha: 0,
      duration: 700,
      delay: 380,
      ease: 'Cubic.in'
    });
  }

  _renderCountdown() {
    if (this.state.phase !== P_COUNTDOWN) {
      if (this.countdownText.visible) this.countdownText.setVisible(false);
      return;
    }
    const remainingMs = Math.max(0, this.state.phaseLen - this.state.phaseAt);
    const remaining = Math.ceil(remainingMs / 1000);
    const txt = remaining > 0 ? String(remaining) : 'GO!';
    if (this.countdownText.text !== txt) {
      this.countdownText.setText(txt);
      this.tweens.killTweensOf(this.countdownText);
      this.countdownText.setScale(1.5);
      this.countdownText.alpha = 0;
      this.tweens.add({
        targets: this.countdownText,
        scale: 1,
        alpha: 1,
        duration: 280,
        ease: 'Cubic.out'
      });
      this._playSfx(remaining > 0 ? 'hd_tick' : 'hd_go');
    }
    this.countdownText.setVisible(true);
  }

  _renderStatusText() {
    let msg = '';
    if (this.state.phase === P_COUNTDOWN) {
      msg = 'GET READY TO GALLOP!';
    } else if (this.state.phase === P_RACE) {
      msg = 'TAP TO RUN!';
    } else if (this.state.phase === P_END) {
      const wn = this.state.winnerId
        ? (this._playerNames[this.state.winnerId] || this.state.winnerId)
        : '';
      msg = wn + ' WINS! 🏆';
    }

    if (this.statusText.text !== msg) {
      this.statusText.setText(msg);
      if (msg) {
        this.tweens.killTweensOf(this.statusText);
        this.statusText.setScale(1.4);
        this.tweens.add({
          targets: this.statusText,
          scale: 1,
          duration: 260,
          ease: 'Back.out'
        });
      }
    }
  }

  _renderEncouragement() {
    // Just a placeholder hook — the actual encouragement banner is
    // animated in _showEncouragement(). Reserved here in case we want
    // to add an idle pulse later.
  }

  // ─────────────────────────────────────────────────── particles

  _spawnDust(x, y, tier) {
    const palette = tier && tier.color !== 0xffffff
      ? [tier.color, tier.glow, 0xfde68a]
      : [0xfde68a, 0xfbbf24, 0xfef3c7];
    for (let i = 0; i < 3; i++) {
      const a = Math.PI + (Math.random() - 0.5) * 0.7;
      const sp = 30 + Math.random() * 50;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 10,
        color: palette[Math.floor(Math.random() * palette.length)],
        shape: 'cloud',
        size: 6 + Math.random() * 4,
        life: 0, ttl: 0.55,
        rot: 0, vrot: 0, gravity: -25
      });
    }
  }

  _spawnFireSparks(x, y, tier) {
    const color = tier ? tier.color : 0xfacc15;
    for (let i = 0; i < 4; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
      const sp = 90 + Math.random() * 70;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 30,
        color,
        shape: 'spark',
        size: 3 + Math.random() * 2,
        life: 0, ttl: 0.5,
        rot: 0, vrot: 0, gravity: 80
      });
    }
  }

  _spawnComboBurst(x, y, tier) {
    const color = tier ? tier.color : 0xfacc15;
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * Math.PI * 2;
      const sp = 140 + Math.random() * 80;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        color,
        shape: 'circle',
        size: 4 + Math.random() * 3,
        life: 0, ttl: 0.6,
        rot: 0, vrot: 0, gravity: 60
      });
    }
    for (let i = 0; i < 10; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 90;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 30,
        color: 0xffffff,
        shape: 'sparkle',
        size: 3,
        life: 0, ttl: 0.55,
        rot: 0, vrot: 0, gravity: 0
      });
    }
  }

  _spawnStartBurst() {
    // Curtain of dust at the start line on GO!
    for (let i = 0; i < 30; i++) {
      const x = this._startX + (Math.random() - 0.5) * 30;
      const y = this._trackTop + Math.random() * (this._trackBottom - this._trackTop);
      const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6;
      const sp = 150 + Math.random() * 120;
      this._particles.push({
        x, y,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp,
        color: 0xfde68a,
        shape: 'cloud',
        size: 10 + Math.random() * 6,
        life: 0, ttl: 0.9,
        rot: 0, vrot: 0, gravity: 40
      });
    }
  }

  _spawnFireworks() {
    const W = this.W, H = this.H;
    const colors = [0xef4444, 0xfacc15, 0x3b82f6, 0x10b981, 0xec4899, 0x8b5cf6];
    for (let b = 0; b < 5; b++) {
      const cx = W * (0.15 + Math.random() * 0.7);
      const cy = H * (0.20 + Math.random() * 0.25);
      const color = colors[b % colors.length];
      this.time.delayedCall(b * 320, () => {
        for (let i = 0; i < 38; i++) {
          const a = (i / 38) * Math.PI * 2;
          const sp = 200 + Math.random() * 120;
          this._particles.push({
            x: cx, y: cy,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
            color, shape: 'circle',
            size: 4, life: 0, ttl: 1.4,
            rot: 0, vrot: 0, gravity: 180
          });
        }
        this._playSfx('hd_tick');
      });
    }
  }

  _spawnTapRipple(x, y, color = 0xffffff, alpha = 0.9) {
    const ring = this.add.graphics().setDepth(14);
    ring.lineStyle(4, color, alpha);
    ring.strokeCircle(0, 0, 18);
    ring.x = x;
    ring.y = y;
    this.tweens.add({
      targets: ring,
      scaleX: 2.4,
      scaleY: 2.4,
      alpha: 0,
      duration: 380,
      ease: 'Cubic.out',
      onComplete: () => ring.destroy()
    });
  }

  _updateParticles(dt) {
    const g = this.fxGraphics;
    g.clear();
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i];
      p.life += dt;
      if (p.life >= p.ttl) { this._particles.splice(i, 1); continue; }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      const a = 1 - (p.life / p.ttl);

      if (p.shape === 'circle') {
        g.fillStyle(p.color, a);
        g.fillCircle(p.x, p.y, p.size * (0.3 + a * 0.7));
      } else if (p.shape === 'cloud') {
        g.fillStyle(p.color, a * 0.55);
        g.fillCircle(p.x, p.y, p.size * (1 + p.life * 0.8));
      } else if (p.shape === 'spark') {
        g.fillStyle(p.color, a);
        g.fillRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size);
      } else if (p.shape === 'sparkle') {
        g.fillStyle(0xffffff, a);
        g.fillCircle(p.x, p.y, p.size * a);
        g.lineStyle(2 * a, 0xffffff, a * 0.8);
        g.strokeCircle(p.x, p.y, p.size * 2 + (1 - a) * 5);
      }
    }
  }

  // ─────────────────────────────────────────────────── local input

  _setupLocalInput() {
    // Whole-screen tap = one gallop step. Sends a one-shot 'hd-tap' event
    // over the SDK bus rather than latching the virtual fire button —
    // single-shot semantics, no per-tick serialization timing to worry
    // about.
    const onTap = (p) => {
      if (this.state.matchOver) return;
      if (this.state.phase !== P_RACE) {
        // During countdown, give a small visual ack so the player knows
        // their tap was received but doesn't count yet.
        if (p) this._spawnTapRipple(p.x, p.y, 0xffffff, 0.4);
        return;
      }
      this._fireLocalTap();
      if (p) this._spawnTapRipple(p.x, p.y, 0xffffff, 0.9);
    };

    this.input.on('pointerdown', onTap);

    // Keyboard fallback (Space + Enter).
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-SPACE', () => onTap(null));
      this.input.keyboard.on('keydown-ENTER', () => onTap(null));
    }
  }

  _fireLocalTap() {
    const playerId = this.sdk.localPlayerId;
    const payload = { playerId, ts: Date.now() };
    if (this.sdk.isHost) {
      this._tapQueue.push(payload);
    } else {
      this.sdk.emit('hd-tap', payload);
    }
    // Optimistic local hoof beat — we play it immediately on every peer
    // so the audio stays in sync with the player's finger rather than
    // waiting for the snapshot round-trip.
    this._playSfx('hd_hoof');
  }

  // ─────────────────────────────────────────────────── music

  _startMusic() {
    if (this.sdk.audio && this.sdk.audio.playMusic) {
      try { this.sdk.audio.playMusic('hd_music'); } catch (_) { /* ignore */ }
    }
  }

  _teardown() {
    try { if (this.sdk.audio && this.sdk.audio.stopMusic) this.sdk.audio.stopMusic(); } catch (_) {}
    try { if (this._unsubTap) this._unsubTap(); } catch (_) {}
  }
}

// ───────────────────────────────────────────────────────── helpers

function round3(v) { return Math.round(v * 1000) / 1000; }
